import { hasAnswerEvaluation } from 'utils/answerEvaluation';
import { hasQuestionGeneration } from 'utils/questionGeneration';
import { normalizeQuestionType } from 'utils/questionTypeUtils';
import { isQuestionResponseValid } from 'utils/questionResponseValidation';
import type { Question, ResponsePayload } from '../../types';
import { areResponsesEqual, FlowResponseDraft, normalizeResponsePayload, StoredResponse, storedToDraft } from './responseUtils';

/** A blank answer on an optional, non-AI item is an explicit skip that the backend counts as done. */
const isBlankOptionalSkip = (question: Question, draft: FlowResponseDraft): boolean => {
  const questionType = normalizeQuestionType(question.question_type_display ?? question.question_type ?? '');
  // Roleplay completion is owned by the server's confirmed-win check.
  if (question.is_required !== false || questionType === 'roleplay'
      || hasAnswerEvaluation(question) || hasQuestionGeneration(question)) {
    return false;
  }
  const answer = normalizeResponsePayload(draft, questionType);
  return answer.rating === null && !answer.text.trim() && answer.bool === null && !answer.selected.length
    && !answer.revealed.length && !(answer.custom ?? '').trim() && !answer.audio
    && !answer.textList.some((item) => item.trim());
};

export const shouldForceComplete = (
  question: Question,
  draft: FlowResponseDraft,
  markComplete: boolean,
): boolean => {
  if (!markComplete) {
    return false;
  }

  if (draft.is_completed === true) {
    return false;
  }

  return isQuestionResponseValid(question, { draft }) || isBlankOptionalSkip(question, draft);
};

export const getWorkingDraft = (
  question: Question,
  draft: FlowResponseDraft,
  markComplete: boolean,
): { workingDraft: FlowResponseDraft; shouldForceComplete: boolean } => {
  const forceComplete = shouldForceComplete(question, draft, markComplete);
  const workingDraft = forceComplete ? { ...draft, is_completed: true } : draft;

  return {
    workingDraft,
    shouldForceComplete: forceComplete,
  };
};

export const shouldDeferIncompleteCustomOtherPersist = (
  question: Question,
  draft: FlowResponseDraft,
): boolean => {
  const selectedOptions = Array.isArray(draft.selected_options) ? draft.selected_options : [];
  if (!selectedOptions.length) {
    return false;
  }

  const hasSelectedOtherOption = (question.options ?? []).some(
    (option) =>
      option.is_other === true
      && typeof option.id === 'number'
      && selectedOptions.includes(option.id),
  );

  if (!hasSelectedOtherOption) {
    return false;
  }

  return typeof draft.custom_other_value !== 'string' || draft.custom_other_value.trim().length === 0;
};

export const applyStoredResponse = (
  stored: StoredResponse,
  workingDraft: FlowResponseDraft,
  forceComplete: boolean,
): { stored: StoredResponse; draft: FlowResponseDraft } => {
  const needsCompletionPatch = forceComplete && workingDraft.is_completed === true && stored.is_completed !== true;
  const normalizedStored = needsCompletionPatch ? { ...stored, is_completed: true } : stored;
  const draftFromStored = storedToDraft(normalizedStored);

  if (forceComplete && workingDraft.is_completed === true && draftFromStored.is_completed !== true) {
    return {
      stored: normalizedStored,
      draft: {
        ...draftFromStored,
        is_completed: true,
      },
    };
  }

  return {
    stored: normalizedStored,
    draft: draftFromStored,
  };
};

// Check if the user has modified the draft since it was last saved.
export const hasUserModifiedDraft = (
  savedDraft: FlowResponseDraft,
  currentDraft: FlowResponseDraft,
): boolean => {
  if (savedDraft.text_value !== currentDraft.text_value) {
    return true;
  }

  if (savedDraft.rating_value !== currentDraft.rating_value) {
    return true;
  }

  if (savedDraft.boolean_value !== currentDraft.boolean_value) {
    return true;
  }

  const savedList = savedDraft.text_list ?? [];
  const currentList = currentDraft.text_list ?? [];
  if (
    savedList.length !== currentList.length
    || savedList.some((item, idx) => item !== currentList[idx])
  ) {
    return true;
  }

  const savedOptions = savedDraft.selected_options ?? [];
  const currentOptions = currentDraft.selected_options ?? [];
  if (
    savedOptions.length !== currentOptions.length
    || savedOptions.some((item, idx) => item !== currentOptions[idx])
  ) {
    return true;
  }

  if (savedDraft.custom_other_value !== currentDraft.custom_other_value) {
    return true;
  }

  return false;
};

export const shouldSkipPersist = (
  payload: ResponsePayload,
  existing: StoredResponse | undefined,
  normalizedQuestionType: string,
): existing is StoredResponse => {
  if (!existing) {
    return false;
  }

  return areResponsesEqual(payload, existing, normalizedQuestionType);
};

export const deriveDraftFromOverride = (
  baseDraft: FlowResponseDraft | undefined,
  override: ResponsePayload,
  questionId: number,
  flowItemId: number | undefined,
  flowRunId: number | null | undefined,
): FlowResponseDraft => {
  const draftFromOverride: FlowResponseDraft = {
    ...(baseDraft ?? { question: questionId }),
    ...override,
    question: questionId,
    responseId: baseDraft?.responseId,
  };

  if (Array.isArray(override.selected_options)) {
    draftFromOverride.selected_options = [...override.selected_options];
  } else if (override.selected_options === undefined && baseDraft?.selected_options) {
    draftFromOverride.selected_options = [...baseDraft.selected_options];
  }

  if (Array.isArray(override.revealed_options)) {
    draftFromOverride.revealed_options = [...override.revealed_options];
  } else if (override.revealed_options === undefined && baseDraft?.revealed_options) {
    draftFromOverride.revealed_options = [...baseDraft.revealed_options];
  }

  if (typeof draftFromOverride.flow_item !== 'number') {
    draftFromOverride.flow_item = baseDraft?.flow_item ?? flowItemId;
  }

  if (typeof draftFromOverride.flow_run !== 'number') {
    draftFromOverride.flow_run = baseDraft?.flow_run ?? flowRunId ?? undefined;
  }

  return draftFromOverride;
};

// Extract a user-facing message from an API error.
export const getErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object') {
    const maybeResponse = (error as { response?: { data?: { error?: string; detail?: string } } }).response?.data;
    if (maybeResponse) {
      if (typeof maybeResponse.error === 'string' && maybeResponse.error.trim()) {
        return maybeResponse.error;
      }
      if (typeof maybeResponse.detail === 'string' && maybeResponse.detail.trim()) {
        return maybeResponse.detail;
      }
    }

    const fallbackMessage = (error as { message?: unknown }).message;
    if (typeof fallbackMessage === 'string' && fallbackMessage.trim()) {
      return fallbackMessage;
    }
  }

  return 'Something went wrong while processing the module.';
};
